import React, { useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from "react-intl";
import { v4 as uuidv4 } from 'uuid';

import {
  sendAIMessage,
  initializeAIChatUI,
  fetchChatHistory,
  syncHistoryFromOtherSession,
  setInitializationState,
  setSubscription,
  setSubscriptionConnected,
  handleRealtimeEvent,
  setError,
  setSessionId,
  setBrowserSessionId,
  retryAIMessage,
  fetchSuggestions,
  startSuggestionsTimer,
  stopSuggestionsTimer,
  setSuggestionsShown,
  markAIConversationAsRead,
  resetUnreadCount,
} from "./redux/aiChatListSlice";
import { InitializationState, AiMessageType, SESSION_ID_KEY } from "./constants";
import AIMessageList from "./components/AIMessageList";
import { subscribeToAIEvents } from "./services/aiChatService";

import "Modules/chat/index.scss";

const SUGGESTIONS_TIMER_DURATION = 30000; // 30 seconds

/**
 * Generate or retrieve browser session ID for this tab
 * Uses sessionStorage so each tab has its own unique ID
 * @returns {string} Session ID (format: sess-{uuid})
 */
const getOrCreateBrowserSessionId = () => {
  let sessionId = sessionStorage.getItem(SESSION_ID_KEY);
  if (!sessionId) {
    sessionId = `sess-${uuidv4()}`;
    sessionStorage.setItem(SESSION_ID_KEY, sessionId);
    console.log('🆔 [Session] Generated new browser sessionId:', sessionId);
  }
  return sessionId;
};

const AIChatList = ({ openModal, onClose }) => {
  const dispatch = useDispatch();
  const { messages, locale } = useIntl();
  const {
    items,
    loadingState,
    loading,
    conversationId,
    browserSessionId,
    currentStage,
    stageMessage,
    isStreaming,
    error,
    initializationState,
    suggestionsShown,
    subscriptionConnected,
  } = useSelector((state) => state.aiChatListRedux);
  
  const { isExpanded } = useSelector((state) => state.chatsContainerRedux);
  const { authId } = useSelector((state) => state.securityRedux);

  // Track if initialization has been done to prevent re-initialization
  const isInitialized = React.useRef(false);
  
  // Track suggestions timer
  const suggestionsTimerRef = React.useRef(null);
  
  // Track if timer has been started for current modal session
  const timerStartedRef = React.useRef(false);
  
  // Track subscription connecting state
  const [subscriptionConnecting, setSubscriptionConnecting] = React.useState(false);

  // Track if markAsRead has been called for current modal session
  const markAsReadCalledRef = React.useRef(false);

  // Track previous isStreaming value to detect COMPLETE transition
  const prevIsStreamingRef = React.useRef(false);

  // Get user context from auth
  const currentUser = useSelector((state) => state.securityRedux?.user);

  // Memoize userContext to prevent unnecessary re-renders
  // NOTE: Must be defined before subscribeWithConnection which uses it
  const userContext = React.useMemo(() => ({
    userEmail: currentUser?.email || '',
    userId: authId || currentUser?.subUserId,
    companyId: currentUser?.companyId || '',
    // with our code branch, we will set domain GPST when calling to AI Chat API.
    // whenever AI Chat implementation available on ignition code branch, we will set domain to ZONAR when calling to AI Chat API
    domain: 'ZONAR', 
  }), [currentUser?.email, currentUser?.userId, currentUser?.id, currentUser?.companyId, currentUser?.domain, authId]);

  /**
   * Helper: Get the last message ID from items (excluding welcome/suggestion messages)
   * @param {Array} messageItems - Array of message items
   * @returns {string|null} Last message ID
   */
  const getLastMessageId = useCallback((messageItems) => {
    if (!messageItems || messageItems.length === 0) return null;
    
    // Filter out welcome and suggestion messages, get actual chat messages
    const chatMessages = messageItems.filter(
      item => item.type !== AiMessageType.WELCOME && item.type !== AiMessageType.SUGGESTION
    );
    
    if (chatMessages.length === 0) return null;
    
    // Get the last message (most recent by createdOn)
    const sortedMessages = [...chatMessages].sort(
      (a, b) => new Date(b.createdOn) - new Date(a.createdOn)
    );
    
    return sortedMessages[0]?.messageId || sortedMessages[0]?.id || null;
  }, []);

  /**
   * Helper: Mark conversation as read when modal opens
   * Should be called when:
   * 1. Modal reopens with cached history
   * 2. After fetching history with messages
   */
  const markConversationRead = useCallback((convId, messageItems, userId) => {
    // Skip if already called for this modal session
    if (markAsReadCalledRef.current) {
      console.log('⏭️ [MarkAsRead] Already called for this session, skipping');
      return;
    }
    
    const lastMessageId = getLastMessageId(messageItems);
    
    if (!convId || !lastMessageId || !userId) {
      console.log('⏭️ [MarkAsRead] Missing required params:', { convId, lastMessageId, userId });
      return;
    }
    
    console.log('📖 [MarkAsRead] Marking conversation as read:', { convId, lastMessageId, userId });
    markAsReadCalledRef.current = true;
    
    // Reset unread count immediately (optimistic update)
    dispatch(resetUnreadCount());
    
    // Call the API to mark as read
    dispatch(markAIConversationAsRead({
      conversationId: convId,
      lastMessageId,
      userId,
    }));
  }, [dispatch, getLastMessageId]);

  /**
   * Helper: Subscribe to events and return a promise that resolves when WebSocket is connected
   * Uses AppSync's CONNECTED control message to detect actual WebSocket handshake completion
   * @param {string} convId - Conversation ID to subscribe to
   * @param {string} sessionId - Browser session ID for event filtering
   */
  const subscribeWithConnection = useCallback((convId, sessionId) => {
    console.log('🔌 [WebSocket] Creating subscription:', { conversationId: convId, browserSessionId: sessionId });
    
    // Set connecting state
    setSubscriptionConnecting(true);
    
    let resolveConnection;
    let rejectConnection;
    
    const connectionPromise = new Promise((resolve, reject) => {
      resolveConnection = resolve;
      rejectConnection = reject;
    });

    // Set a timeout in case CONNECTED message never arrives (network issues)
    const connectionTimeout = setTimeout(() => {
      console.warn('⚠️ [WebSocket] Connection timeout - CONNECTED message not received within 30s');
      console.warn('⚠️ [WebSocket] Input will remain disabled until connection is established');
      // Don't force enable - let user see the connection issue
      rejectConnection(new Error('WebSocket connection timeout'));
    }, 30000);

    const subscription = subscribeToAIEvents(
      convId,
      (event) => {
        dispatch(handleRealtimeEvent({ event }));
      },
      (error) => {
        console.error('❌ [WebSocket] Subscription error:', error);
        clearTimeout(connectionTimeout);
        setSubscriptionConnecting(false);
        dispatch(setSubscriptionConnected(false));
        dispatch(setError(error.message || 'Subscription error'));
        rejectConnection(error);
      },
      () => {
        // onConnected callback - fires when CONNECTED control message received
        console.log('✅ [WebSocket] Connected - handshake complete');
        clearTimeout(connectionTimeout);
        setSubscriptionConnecting(false);
        dispatch(setSubscriptionConnected(true));
        resolveConnection();
      },
      // Options for session-based filtering
      {
        browserSessionId: sessionId,
        onOtherSessionComplete: async (event) => {
          // Another browser tab completed streaming - sync history without full reload
          console.log('📥 [Sync] Another session completed, syncing new messages...');
          try {
            await dispatch(syncHistoryFromOtherSession({ 
              userId: userContext.userId,
            }));
            console.log('✅ [Sync] History synced successfully');
          } catch (error) {
            console.error('❌ [Sync] Failed to sync history:', error);
          }
        },
      }
    );

    dispatch(setSubscription(subscription));
    return connectionPromise;
  }, [dispatch, userContext.userId, messages]);

  // Start suggestions timer
  const startTimer = useCallback(() => {
    // Clear any existing timer
    if (suggestionsTimerRef.current) {
      clearTimeout(suggestionsTimerRef.current);
    }

    console.log('⏱️ [Timer] Starting 30-second suggestions timer');
    dispatch(startSuggestionsTimer());

    suggestionsTimerRef.current = setTimeout(() => {
      console.log('⏰ [Timer] 30 seconds elapsed, fetching suggestions');
      
      // Convert locale to uppercase (e.g., 'en' -> 'EN', 'es' -> 'ES')
      // Handle cases where locale might be a string or undefined
      let localeStr = 'en'; // default
      if (typeof locale === 'string') {
        localeStr = locale;
      } else if (locale && typeof locale === 'object') {
        // If locale is an object, log it and use default
        console.warn('⚠️ [Timer] Locale is an object, using default "en":', locale);
      }
      const localeUpper = localeStr.toUpperCase();
      
      console.log('🌐 [Timer] Fetching suggestions with locale:', localeUpper);
      
      // Fetch suggestions from backend
      dispatch(fetchSuggestions({
        domain: userContext.domain,
        topic: 'fleet',
        locale: localeUpper,
        translations: messages,
      }));

      suggestionsTimerRef.current = null;
    }, SUGGESTIONS_TIMER_DURATION);
  }, [dispatch, suggestionsShown, userContext.domain, locale]);

  // Stop suggestions timer
  const stopTimer = useCallback(() => {
    if (suggestionsTimerRef.current) {
      console.log('⏹️ [Timer] Stopping suggestions timer');
      clearTimeout(suggestionsTimerRef.current);
      suggestionsTimerRef.current = null;
      dispatch(stopSuggestionsTimer());
    }
  }, [dispatch]);

  // Load initial suggestions and history
  useEffect(() => {
    if (!openModal) {
      // Reset initialization and timer flags when modal closes
      isInitialized.current = false;
      timerStartedRef.current = false;
      markAsReadCalledRef.current = false; // Reset mark as read flag
      // Stop timer and reset suggestions flag
      stopTimer();
      dispatch(setSuggestionsShown(false));
      return;
    }

    if (openModal && userContext.userId) {
      // Check if already initialized to prevent re-fetching
      if (isInitialized.current) {
        return;
      }

      // Check if items already exist in Redux (reopening modal with cached history)
      if (items.length > 0 && conversationId) {
        // ✅ FIX: Ensure WebSocket is connected before allowing interaction
        if (!subscriptionConnected) {
          console.log('🔄 [Init] Reopening modal - reconnecting WebSocket...');
          dispatch(setInitializationState(InitializationState.SKELETON));
          // Get or create browser session ID for this tab
          const sessionId = getOrCreateBrowserSessionId();
          dispatch(setBrowserSessionId(sessionId));
          subscribeWithConnection(conversationId, sessionId)
            .then(() => {
              console.log('✅ [Init] WebSocket reconnected for existing conversation');
              dispatch(setInitializationState(InitializationState.WELCOME_READY));
              // Mark conversation as read when modal opens with cached history
              markConversationRead(conversationId, items, userContext.userId);
            })
            .catch((error) => {
              console.error('❌ [Init] WebSocket reconnection failed:', error);
              // Still show UI but user may have issues
              dispatch(setInitializationState(InitializationState.WELCOME_READY));
              // Still mark as read even if WebSocket failed
              markConversationRead(conversationId, items, userContext.userId);
            });
        } else {
          dispatch(setInitializationState(InitializationState.WELCOME_READY));
          // Mark conversation as read when modal opens with cached history
          markConversationRead(conversationId, items, userContext.userId);
        }
        isInitialized.current = true;
        
        // Reset timer flag and suggestions flag so timer can run when modal reopens
        timerStartedRef.current = false;
        dispatch(setSuggestionsShown(false));
        return;
      }

      // Mark as initialized to prevent re-triggering
      isInitialized.current = true;

      // ✅ CORRECT ORDER: Connect WebSocket FIRST, then fetch data
      const initializeChat = async () => {
        console.log('🚀 [Init] Starting AI Chat initialization...');
        
        // Step 1: Generate browser session ID for this tab (once per tab lifecycle)
        const sessionId = getOrCreateBrowserSessionId();
        dispatch(setBrowserSessionId(sessionId));
        
        // Step 2: Show skeleton immediately
        dispatch(setInitializationState(InitializationState.SKELETON));
        
        // Step 3: Generate a temporary conversationId for initial connection
        const tempConversationId = uuidv4();
        dispatch(setSessionId(tempConversationId));
        
        // Step 4: Connect WebSocket FIRST (await connection with timeout)
        console.log('🔌 [Init] Step 1: Connecting WebSocket...');
        try {
          await subscribeWithConnection(tempConversationId, sessionId);
          console.log('✅ [Init] WebSocket connected successfully');
        } catch (error) {
          console.error('❌ [Init] WebSocket connection failed:', error);
          // Continue anyway - the subscription might connect later
        }
        
        // Step 4: THEN fetch history (WebSocket is now ready or timed out)
        console.log('📚 [Init] Step 2: Fetching chat history...');
        try {
          const result = await dispatch(fetchChatHistory({ userId: userContext.userId, messages }));
          
          // Step 5: Handle result
          if (result.type === 'aiChatListRedux/fetchChatHistory/fulfilled') {
            const payload = result.payload || {};
            const hasData = Array.isArray(payload.items) && 
              payload.items.filter(item => item.type !== AiMessageType.WELCOME).length > 0;
            const historyConversationId = payload.conversationId;
            
            console.log('📦 [Init] History result:', { hasData, historyConversationId });
            
            if (hasData && historyConversationId) {
              // Case 1: Has history - need to resubscribe with correct conversationId
              if (historyConversationId !== tempConversationId) {
                console.log('🔄 [Init] Resubscribing with history conversationId...');
                dispatch(setSessionId(historyConversationId));
                try {
                  await subscribeWithConnection(historyConversationId, sessionId);
                  console.log('✅ [Init] Resubscribed with history conversationId');
                } catch (error) {
                  console.error('❌ [Init] Resubscribe failed:', error);
                }
              }
              // Show history (already loaded by fetchChatHistory)
              dispatch(setInitializationState(InitializationState.WELCOME_READY));
              // Mark conversation as read after showing history
              markConversationRead(historyConversationId, payload.items, userContext.userId);
            } else {
              // Case 2: No history - show welcome (WebSocket already connected)
              console.log('👋 [Init] No history, showing welcome message...');
              dispatch(initializeAIChatUI({ 
                translations: messages, 
                subscriptionConnectedPromise: Promise.resolve() // Already connected
              }));
            }
          } else {
            // Case 3: Error fetching history - show welcome anyway
            console.log('⚠️ [Init] History fetch failed, showing welcome...');
            dispatch(initializeAIChatUI({ 
              translations: messages, 
              subscriptionConnectedPromise: Promise.resolve()
            }));
          }
        } catch (error) {
          console.error('❌ [Init] Initialization error:', error);
          dispatch(initializeAIChatUI({ 
            translations: messages, 
            subscriptionConnectedPromise: Promise.resolve()
          }));
        }
      };
      
      initializeChat();
    } else if (openModal) {
      // Check if already initialized
      if (isInitialized.current) {
        return;
      }

      // Mark as initialized
      isInitialized.current = true;

      // Modal opened but no userId yet - generate conversationId and show init chat
      const initWithoutUser = async () => {
        console.log('🚀 [Init] Starting AI Chat without userId...');
        
        // Generate browser session ID for this tab
        const sessionId = getOrCreateBrowserSessionId();
        dispatch(setBrowserSessionId(sessionId));
        
        dispatch(setInitializationState(InitializationState.SKELETON));
        
        const newConversationId = uuidv4();
        dispatch(setSessionId(newConversationId));
        
        // Connect WebSocket first
        try {
          await subscribeWithConnection(newConversationId, sessionId);
          console.log('✅ [Init] WebSocket connected (no user)');
        } catch (error) {
          console.error('❌ [Init] WebSocket connection failed (no user):', error);
        }
        
        dispatch(initializeAIChatUI({ 
          translations: messages, 
          subscriptionConnectedPromise: Promise.resolve()
        }));
      };
      
      initWithoutUser();
    }
  }, [openModal, dispatch, userContext.userId, items, conversationId, subscriptionConnected, messages, subscribeWithConnection, stopTimer, markConversationRead]);

  /**
   * Timer Management Effect
   * Start timer when AI Chat opens, stop when closed/collapsed
   * Only for new users without conversation history
   */
  useEffect(() => {
    // Check if suggestions already exist in items
    const hasSuggestionItems = items.some(item => item.type === AiMessageType.SUGGESTION);
    
    // Check for real conversation history (excluding welcome/suggestion messages)
    const hasConversationHistory = items.some(
      item => item.type !== AiMessageType.WELCOME && 
              item.type !== AiMessageType.SUGGESTION
    );
    
    // Start timer when modal opens, is expanded, initialization is ready,
    // suggestions haven't been shown, AND user has no conversation history
    const isReadyToStart = openModal && 
                          isExpanded && 
                          initializationState === InitializationState.WELCOME_READY &&
                          !timerStartedRef.current && 
                          !suggestionsShown &&
                          !hasSuggestionItems &&
                          !hasConversationHistory;
    
    if (isReadyToStart) {
      console.log('🟢 [Timer Effect] New user without history, starting suggestions timer');
      timerStartedRef.current = true;
      startTimer();
    } else if (!openModal || !isExpanded) {
      // Stop timer when modal closes or is collapsed
      console.log('🔴 [Timer Effect] Modal closed or collapsed, stopping timer');
      stopTimer();
      // Reset timer flag so it can run again on next open
      timerStartedRef.current = false;
    } else if (hasConversationHistory) {
      console.log('⏭️ [Timer Effect] User has conversation history, skipping suggestions');
    } else if (hasSuggestionItems) {
      console.log('⏭️ [Timer Effect] Suggestions already exist in items, skipping timer');
    }

    // Cleanup on unmount
    return () => {
      stopTimer();
    };
  }, [openModal, isExpanded, initializationState, startTimer, stopTimer, suggestionsShown, items]);

  /**
   * Stop timer when suggestions are shown
   */
  useEffect(() => {
    if (suggestionsShown) {
      console.log('✅ [Timer Effect] Suggestions shown, stopping timer');
      stopTimer();
    }
  }, [suggestionsShown, stopTimer]);

  /**
   * Mark conversation as read when agent COMPLETE fires while user is still in AI chat.
   * The backend increments unread_count when the agent replies, so we must reset it
   * to avoid stale unread badges appearing when the user later backs out and returns.
   */
  useEffect(() => {
    const prevStreaming = prevIsStreamingRef.current;
    prevIsStreamingRef.current = isStreaming;

    const justFinished = prevStreaming === true && isStreaming === false;

    if (justFinished && openModal && conversationId && userContext.userId) {
      console.log('📖 [MarkAsRead] COMPLETE while in chat → marking conversation as read');
      // Reset guard so markConversationRead can fire again for this new reply
      markAsReadCalledRef.current = false;
      markConversationRead(conversationId, items, userContext.userId);
    }
  }, [isStreaming, openModal, conversationId, items, userContext.userId, markConversationRead]);



  /**
   * Handle send message
   * - Always call sendChatMessage with existing conversationId
   * - Subscription is already active from useEffect
   */
  const handleSendMessage = useCallback(
    async (message) => {
      if (!message?.trim()) {
        return;
      }

      if (!conversationId) {
        console.error('No conversationId available');
        return;
      }

      // Stop suggestions timer when user sends a message
      stopTimer();

      const payload = {
        prompt: message.trim(),
        conversationId, // Use existing conversationId (from history or generated)
        ...userContext,
      };
      
      // Always use sendAIMessage which calls sendChatMessage with existing conversationId
      await dispatch(sendAIMessage(payload));
    },
    [dispatch, conversationId, userContext, stopTimer]
  );

  const handleRetryPrompt = useCallback(({ errorItem }) => {
    // Find the user prompt by matching correlationId
    const retryPromptIndex = items.findIndex(item => item.sender === 'user' && item.correlationId === errorItem.correlationId);
    const retryItem = items[retryPromptIndex];

    if (retryItem?.sender === 'user' && retryItem.message) {
      const payload = {
        errorId: errorItem?.id || '', // send the error item for context
        prompt: retryItem.message, // retry message
        conversationId, // Use existing conversationId (from history or generated)
        ...userContext,
      };
      dispatch(retryAIMessage(payload));
    }
  }, [dispatch, items, isStreaming, currentStage, conversationId, userContext]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <AIMessageList
      messages={messages}
      openModal={openModal}
      onClose={handleClose}
      items={items}
      loadingState={loadingState}
      loading={loading}
      currentStage={currentStage}
      stageMessage={stageMessage}
      isStreaming={isStreaming}
      error={error}
      initializationState={initializationState}
      submitQuestion={handleSendMessage}
      handleRetryPrompt={handleRetryPrompt}
      subscriptionConnected={subscriptionConnected}
      subscriptionConnecting={subscriptionConnecting}
    />
  );
};

export default AIChatList;
